import { LoadingButton } from '@mui/lab';
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	Icon,
	List,
	ListItem,
	ListItemText,
	MenuItem,
	Paper,
	TextField,
	Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks';
import { useI18N } from '../i18n';
import { useRedux } from '../redux';
import { useAuth } from '../redux/slices/auth';
import { useDispatch } from 'react-redux';
import { createTicket, updateTicket } from '../redux/slices/tickets';
import { uploadAttachments, useAttachments } from '../redux/slices/attachments';
import { getGroups, Group } from '../redux/slices/groups';
import {
	TICKET_CATEGORIES,
	TICKET_ENVIRONMENTS,
	TICKET_FINANCIAL_IMPACTS,
	TICKET_IMPACTED_USERS,
	TICKET_OPERATION_STATUSES,
	TICKET_PRIORITIES,
	TICKET_PRODUCTS,
	TICKET_TYPES,
	TICKET_GROUPS,
	useTickets,
} from '../redux/slices/tickets';
import { Api } from '../services/Api';
import { FileDropArea } from './FileDropArea';
import { RichTextEditor } from './RichTextEditor';
import type { I18NKey } from '../i18n';
import type {
	TicketCategory,
	TicketEnvironment,
	TicketFinancialImpact,
	TicketImpactedUsers,
	TicketOperationStatus,
	TicketPriority,
	TicketProduct,
	TicketType,
	TicketGroup,
} from '../redux/slices/tickets';
import type { GetValuesFn } from './RichTextEditor';

import { createTicketTemplate } from '../templates/Email/createTicketTemplate';
import filesToAttachments from '../functions/filesToAttachments';
import { useGroups } from '../redux/slices/groups';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { appcenterFirestore } from '../services/firebase';
import Inputmask from 'inputmask';
import { addCommentToTicket } from '../redux/slices/comments';
import { autoReplyCreateTicket } from '../templates/Comments/autoReplyCreateTicket';
import UpdatePhoneModal from './UpdatePhoneModal';

export interface TicketFormErrors {
	title: I18NKey;
	description: I18NKey;
	shortDescription: I18NKey;
	group: I18NKey;
	type: I18NKey;
	product: I18NKey;
	category: I18NKey;
	environment: I18NKey;
	operationStatus: I18NKey;
	impactedUsers: I18NKey;
	financialImpact: I18NKey;
	priority: I18NKey;
}

interface AssociatedUser {
	uid: string;
	firstName?: string;
	email: string;
}

interface AssociatedCompany {
	id: string;
	name: string;
	cnpj: string;
	identificationNumber: string;
}

export const TicketForm = (): JSX.Element => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const i18n = useI18N();
	const isMobile = useIsMobile();
	const { snackbar, spinner } = useRedux();
	const { user, company, isTaugorCompany } = useAuth();
	const { loading, ticket } = useTickets();
	const { loading: LoadingAttachments } = useAttachments();
	const { groups, loading: loadingGroups } = useGroups();

	const [searchType, setSearchType] = useState<'identificationNumber' | 'primaryEmail'>(
		'identificationNumber'
	);
	const maskFields = ['identificationNumber'];

	const [searchValue, setSearchValue] = useState('');
	const [companies, setCompanies] = useState<any[]>([]);
	const [isFranchiseTicket, setIsFranchiseTicket] = useState(false);

	const [loadingSendEmail, setLoadingSendEmail] = useState(false);
	const [title, setTitle] = useState('');
	const [descriptionRaw, setDescriptionRaw] = useState('');
	const [shortDescription, setShortDescription] = useState('');
	const [group, setGroup] = useState<Group | null>(null);
	const [type, setType] = useState<TicketType | null>(null);
	const [product, setProduct] = useState<TicketProduct | null>(null);
	const [companyUsers, setCompanyUsers] = useState<any[]>([]);
	const [associatedCompany, setAssociatedCompany] = useState<AssociatedCompany | null>(null);
	const [associatedUser, setAssociatedUser] = useState<AssociatedUser | null>(null);
	const [ccEmails, setCcEmails] = useState<string | null>(null);
	const [category, setCategory] = useState<TicketCategory | null>(null);
	const [environment, setEnvironment] = useState<TicketEnvironment | null>(null);
	const [operationStatus, setOperationStatus] = useState<TicketOperationStatus | null>(null);
	const [impactedUsers, setImpactedUsers] = useState<TicketImpactedUsers | null>(null);
	const [financialImpact, setFinancialImpact] = useState<TicketFinancialImpact | null>(null);
	const [priority, setPriority] = useState<TicketPriority | null>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [errors, setErrors] = useState<TicketFormErrors>({
		title: '',
		description: '',
		shortDescription: '',
		group: '',
		type: '',
		product: '',
		category: '',
		environment: '',
		operationStatus: '',
		impactedUsers: '',
		financialImpact: '',
		priority: '',
	});
	const [showPhoneModal, setShowPhoneModal] = useState(false);
	const [skipPhoneCheck, setSkipPhoneCheck] = useState(false);

	const getDescriptionValuesRef = useRef<GetValuesFn>(null);
	const cnpjRef = useRef<HTMLInputElement>(null);

	const skipPhoneCheckRef = useRef(false);

	useEffect(() => {
		skipPhoneCheckRef.current = skipPhoneCheck;
	}, [skipPhoneCheck]);

	useEffect(() => {
		if (skipPhoneCheck) {
			// dados atualizados, segue com fluxo
			handleCreateTicket();
		}
	}, [user?.phone, user?.phoneArea]);

	useEffect(() => {
		setAssociatedCompany(null);
		setAssociatedUser(null);
		setCcEmails(null);
		if (searchType === 'identificationNumber' && cnpjRef.current) {
			Inputmask('99.999.999/9999-99').mask(cnpjRef.current);
		}
	}, [searchType, isFranchiseTicket]);

	useEffect(() => {
		if (!associatedCompany?.id) return;

		const fetchCompanyUsers = async () => {
			const usersRef = collection(appcenterFirestore, 'user');
			const q = query(usersRef, where('active', 'array-contains', associatedCompany.id));
			const snapshot = await getDocs(q);

			const results = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));

			setCompanyUsers(results);
		};

		fetchCompanyUsers();
	}, [associatedCompany]);

	useEffect(() => {
		dispatch(getGroups());
	}, [dispatch]);

	const availableGroups = useMemo(() => {
		// Se for franquia, retorna todos
		if (company?.isFranchisee) {
			return groups;
		}

		// Se não for franquia, retorna apenas os que NÃO são exclusivos de franquia
		return groups.filter((g) => !g.isFranchise);
	}, [groups, user]);

	const handleSearch = async () => {
		try {
			if (!searchValue.trim()) {
				setCompanies([]);
				return;
			}

			const collectionRef = collection(appcenterFirestore, 'company');

			const value =
				searchType === 'identificationNumber' ? searchValue : searchValue.trim().toLowerCase();

			const q = query(collectionRef, where(searchType, '==', value));

			const snapshot = await getDocs(q);

			if (snapshot.empty) {
				console.log('Nenhuma empresa encontrada.');
				setCompanies([]);
			} else {
				const results = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}));
				setCompanies(results);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const handleClear = () => {
		setTitle('');
		setDescriptionRaw('');
		setShortDescription('');
		setGroup(null);
		setType(null);
		setProduct(null);
		setCategory(null);
		setEnvironment(null);
		setOperationStatus(null);
		setImpactedUsers(null);
		setFinancialImpact(null);
		setPriority(null);
		setFiles([]);
		setErrors({
			title: '',
			description: '',
			shortDescription: '',
			group: '',
			type: '',
			product: '',
			category: '',
			environment: '',
			operationStatus: '',
			impactedUsers: '',
			financialImpact: '',
			priority: '',
		});
	};

	const handleCreateTicket = async () => {
		const newErrors: TicketFormErrors = {
			title: '',
			description: '',
			shortDescription: '',
			group: '',
			type: '',
			product: '',
			category: '',
			environment: '',
			operationStatus: '',
			impactedUsers: '',
			financialImpact: '',
			priority: '',
		};
		let hasErrors = false;

		if (!user?.email || !getDescriptionValuesRef.current) {
			snackbar.show({ status: 'error', message: 'tickets.form.errors.create' });

			return;
		}

		const [descriptionRaw, description] = await getDescriptionValuesRef.current();
		setDescriptionRaw(descriptionRaw);

		if (!title) {
			newErrors.title = 'tickets.form.errors.title.empty';
			hasErrors = true;
		}

		if (!description || description === '<p></p>') {
			newErrors.description = 'tickets.form.errors.description.empty';
			hasErrors = true;
		}

		if (!shortDescription) {
			newErrors.shortDescription = 'tickets.form.errors.shortDescription.empty';
			hasErrors = true;
		}

		if (!group) {
			newErrors.group = 'tickets.form.errors.group.empty';
			hasErrors = true;
		}

		if (!type) {
			newErrors.type = 'tickets.form.errors.type.empty';
			hasErrors = true;
		}

		// if (!product) {
		// 	newErrors.product = 'tickets.form.errors.product.empty';
		// 	hasErrors = true;
		// }

		if (!operationStatus) {
			newErrors.operationStatus = 'tickets.form.errors.operationStatus.empty';
			hasErrors = true;
		}

		if (!group || !type || !operationStatus || hasErrors) {
			setErrors(newErrors);

			return;
		}

		const hasPhone = !!user?.phone && !!user?.phoneArea;

		if (!hasPhone && !skipPhoneCheckRef.current) {
			setShowPhoneModal(true);
			return;
		}

		try {
			spinner.show();
			const ccList = ccEmails
				? [
						...ccEmails
							.split(',')
							.map((e: string) => e.trim())
							.filter(Boolean),
				  ]
				: [];

			const response = await dispatch(
				createTicket({
					uid: user.uid,
					title,
					description,
					shortDescription,
					group: group.id ?? '',
					type: type,
					status: 'Aberto',
					email: user.email,
					user: {
						uid: user.uid,
						name: user.name,
					},
					number: '',
					creationDate: Date.now(),
					lastChangeDate: Date.now(),
					product: product ?? '',
					associatedUser: {
						uid: associatedUser?.uid ?? '',
						email: associatedUser?.email ?? '',
						cnpj: associatedCompany?.cnpj ?? '',
						name: associatedUser?.firstName ?? '',
						razao: associatedCompany?.name ?? '',
					},
					razao: company?.name ?? '',
					ccEmails: ccList,
					cnpj: company?.cnpj ?? '',
					phone: `${user.phoneArea}${user.phone}`,
					operationStatus,
				}) as any
			);

			setLoadingSendEmail(true);
			const { ticketId, number: numberTicket } = response.payload;

			if (!response?.payload?.ticketId) {
				throw new Error('Falha ao criar ticket: recarregue a página e tente novamente');
			}

			// send email
			const attachments = await filesToAttachments(files);
			const responseEmail = (await Api.tickets.sendMail({
				to: associatedUser === null ? user.email : associatedUser?.email,
				cc: [group.emailNotification, ...ccList],
				subject: title,
				attachments,
				html: createTicketTemplate(ticketId, numberTicket),
				companyUid: company?.uid ?? '',
			})) as any;

			// if (responseEmail.viewDetectorUid) {
			// 	dispatch(
			// 		updateTicket({
			// 			ticketId: ticketId,
			// 			updateFields: { viewDetectorUid: responseEmail?.viewDetectorUid },
			// 		})
			// 	);
			// }

			setLoadingSendEmail(false);
			// upload attachments
			if (files && files.length > 0) {
				const res = await dispatch(
					uploadAttachments({
						ticketId: ticketId,
						files,
						commentId: '',
					})
				);
			}

			dispatch(
				addCommentToTicket({
					ticketId,
					commentData: {
						content: autoReplyCreateTicket(associatedUser?.firstName ?? user.name),
						user: { uid: 'system', name: 'System Taugor' },
					},
				})
			);

			const phoneNumbers: string[] = [
				...(group?.phoneNumbers ?? []),
				...(user.phone ? [`${user.phoneArea}${user.phone}`] : []),
			];

			if (phoneNumbers.length > 0) {
				await Api.tickets.sendWtsMessage({
					to: phoneNumbers,
					parameters: {
						Razao: associatedCompany?.name ?? company?.name,
						cnpj: associatedCompany?.identificationNumber ?? company?.cnpj,
						'dono do ticket': associatedUser?.firstName ?? user.name,
						categoria: type,
						ticket: numberTicket,
						link: `https://inbox.taugor.app/tickets/${ticketId}`,
					},
				});
			}

			snackbar.show({ status: 'success', message: 'tickets.form.success.create' });
			navigate(`/tickets/${ticketId}`);
		} catch (err) {
			snackbar.show({ status: 'error', message: 'tickets.form.errors.create' });
		} finally {
			spinner.hide();
		}
	};

	return (
		<Paper
			elevation={6}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				gap: 2,
				p: isMobile ? 2 : 4,

				'> *': {
					width: 1,
				},
			}}
		>
			<Typography variant="h5">{i18n('tickets.new')}</Typography>

			<Typography>{i18n('tickets.new.subtitle')}</Typography>

			{isTaugorCompany && (
				<FormControlLabel
					control={
						<Checkbox
							checked={isFranchiseTicket}
							onChange={(e) => setIsFranchiseTicket(e.target.checked)}
						/>
					}
					label="Criar ticket para outro"
				/>
			)}

			{isTaugorCompany && isFranchiseTicket && (
				<Box
					sx={{
						display: 'flex',
						gap: 2,
						alignItems: 'center',
						...(isMobile
							? {
									flexDirection: 'column',
							  }
							: {}),
					}}
				>
					<TextField
						select
						size="small"
						value={searchType}
						onChange={(e) =>
							setSearchType(e.target.value as 'identificationNumber' | 'primaryEmail')
						}
					>
						<MenuItem value="identificationNumber">CNPJ</MenuItem>
						<MenuItem value="primaryEmail">Email da empresa</MenuItem>
					</TextField>

					{maskFields.includes(searchType) && (
						<TextField
							id="title"
							inputRef={cnpjRef}
							label={searchType === 'identificationNumber' ? 'Digite o CNPJ' : 'Digite o E-mail'}
							value={searchValue}
							required
							disabled={loading}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					)}

					{!maskFields.includes(searchType) && (
						<TextField
							id="title"
							label={searchType === 'primaryEmail' ? 'Digite o E-mail' : 'Digite o CNPJ'}
							value={searchValue}
							required
							disabled={loading}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					)}
					<Button variant="contained" size="large" disabled={loading} onClick={handleSearch}>
						Buscar
					</Button>

					{companies.length > 0 ? (
						<Box>
							<Typography>Selecione uma empresa:</Typography>
							{companies.map((company) => (
								<List key={company.id} sx={{ maxWidth: 500 }}>
									<ListItem
										sx={{
											borderRadius: '4px',
											background: associatedCompany?.id === company.id ? '#a4d7ff' : '#eee',
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											gap: 2,
										}}
									>
										<ListItemText>{company.name}</ListItemText>
										<Button
											variant={associatedCompany?.id === company.id ? 'contained' : 'contained'}
											size="small"
											onClick={() => setAssociatedCompany(company)}
										>
											Selecionar
										</Button>
									</ListItem>
								</List>
							))}
							{companyUsers.length > 0 && (
								<Box
									mt={2}
									sx={{
										display: 'flex',
										flexDirection: 'row',
										gap: 2,
										...(isMobile
											? {
													flexDirection: 'column',
											  }
											: {}),
									}}
								>
									<Box>
										<Typography>Selecione o usuário representante:</Typography>
										<TextField
											select
											fullWidth
											value={associatedUser?.uid ?? ''}
											onChange={(e) => {
												const user = companyUsers.find((u) => u.id === e.target.value);
												setAssociatedUser(user);
											}}
										>
											{companyUsers.map((user) => (
												<MenuItem key={user.id} value={user.id}>
													{user.firstName + ' ' + user.lastName} – {user.email}
												</MenuItem>
											))}
										</TextField>
									</Box>
									<Box>
										<Typography>E-mails em cópia (CC):</Typography>
										<TextField
											sx={{ width: '400px', ...(isMobile ? { width: '100%' } : {}) }}
											type="text"
											id="cc"
											name="cc"
											disabled={loading}
											placeholder="exemplo1@empresa.com, exemplo2@empresa.com"
											value={ccEmails ?? ''}
											onChange={(e) => setCcEmails(e.target.value)}
										/>
									</Box>
								</Box>
							)}
						</Box>
					) : (
						<Typography color={'error'}>Empresa não encontrada!</Typography>
					)}
				</Box>
			)}

			<TextField
				id="title"
				label={i18n('tickets.form.title')}
				value={title}
				required
				disabled={loading}
				error={!!errors.title}
				helperText={errors.title ? i18n(errors.title) : ''}
				onChange={(e) => {
					setTitle(e.target.value);

					if (errors.title) {
						setErrors((prevErrors) => ({
							...prevErrors,
							title: '',
						}));
					}
				}}
			/>

			<RichTextEditor
				id="description"
				label="tickets.form.description"
				defaultValue={descriptionRaw}
				isDisabled={loading}
				errorMessage={errors.description}
				getValuesRef={getDescriptionValuesRef}
				onFocus={() => {
					if (errors.description) {
						setErrors((prevErrors) => ({
							...prevErrors,
							description: '',
						}));
					}
				}}
			/>

			<TextField
				id="shortDescription"
				label={i18n('tickets.form.shortDescription')}
				value={shortDescription}
				required
				disabled={loading}
				inputProps={{
					maxLength: 240,
				}}
				error={!!errors.shortDescription}
				helperText={errors.shortDescription ? i18n(errors.shortDescription) : ''}
				onChange={(e) => {
					setShortDescription(e.target.value);

					if (errors.shortDescription) {
						setErrors((prevErrors) => ({
							...prevErrors,
							shortDescription: '',
						}));
					}
				}}
			/>

			<Grid
				container
				spacing={2}
				sx={{
					'> .MuiGrid-item': {
						width: 1,

						'> *': {
							width: 1,
						},
					},
				}}
			>
				<Grid item xs={12} md={3}>
					<Autocomplete
						id="group"
						options={availableGroups}
						value={group}
						disabled={loading}
						disablePortal
						openOnFocus
						getOptionLabel={(option) => option.name}
						renderInput={(params) => (
							<TextField
								{...params}
								label={i18n('tickets.form.group')}
								required
								error={!!errors.group}
								helperText={errors.group ? i18n(errors.group) : ''}
							/>
						)}
						onChange={(e, newValue) => {
							setGroup(newValue);

							if (errors.group) {
								setErrors((prevErrors) => ({
									...prevErrors,
									group: '',
								}));
							}
						}}
					/>
				</Grid>

				{group?.name === 'Marketing' || group?.name === 'Suporte Técnico' ? (
					<Grid item xs={12} md={3}>
						<Autocomplete
							id="product"
							options={TICKET_PRODUCTS}
							value={product}
							disabled={loading}
							disablePortal
							openOnFocus
							renderInput={(params) => (
								<TextField
									{...params}
									label={i18n('tickets.form.product')}
									required
									error={!!errors.product}
									helperText={errors.product ? i18n(errors.product) : ''}
								/>
							)}
							onChange={(e, newValue) => {
								setProduct(newValue);

								if (errors.product) {
									setErrors((prevErrors) => ({
										...prevErrors,
										product: '',
									}));
								}
							}}
						/>
					</Grid>
				) : null}

				<Grid item xs={12} md={3}>
					<Autocomplete
						id="type"
						options={TICKET_TYPES}
						value={type}
						disabled={loading}
						disablePortal
						openOnFocus
						renderInput={(params) => (
							<TextField
								{...params}
								label={i18n('tickets.form.type')}
								required
								error={!!errors.type}
								helperText={errors.type ? i18n(errors.type) : ''}
							/>
						)}
						onChange={(e, newValue) => {
							setType(newValue);

							if (errors.type) {
								setErrors((prevErrors) => ({
									...prevErrors,
									type: '',
								}));
							}
						}}
					/>
				</Grid>

				{/* <Grid item xs={12} md={3}>
					<Autocomplete
						id="category"
						options={TICKET_CATEGORIES}
						value={category}
						disabled={loading}
						disablePortal
						openOnFocus
						renderInput={(params) => (
							<TextField
								{...params}
								label={i18n('tickets.form.category')}
								required
								error={!!errors.category}
								helperText={errors.category ? i18n(errors.category) : ''}
							/>
						)}
						onChange={(e, newValue) => {
							setCategory(newValue);

							if (errors.category) {
								setErrors((prevErrors) => ({
									...prevErrors,
									category: '',
								}));
							}
						}}
					/>
				</Grid> */}

				{/* <Grid item xs={12} md={3}>
					<Autocomplete
						id="environment"
						options={TICKET_ENVIRONMENTS}
						value={environment}
						disabled={loading}
						disablePortal
						openOnFocus
						renderInput={(params) => (
							<TextField
								{...params}
								label={i18n('tickets.form.environment')}
								required
								error={!!errors.environment}
								helperText={errors.environment ? i18n(errors.environment) : ''}
							/>
						)}
						onChange={(e, newValue) => {
							setEnvironment(newValue);

							if (errors.environment) {
								setErrors((prevErrors) => ({
									...prevErrors,
									environment: '',
								}));
							}
						}}
					/>
				</Grid> */}

				<Grid item xs={12} md={3}>
					<Autocomplete
						id="operationStatus"
						options={TICKET_OPERATION_STATUSES}
						value={operationStatus}
						disabled={loading}
						disablePortal
						openOnFocus
						renderInput={(params) => (
							<TextField
								{...params}
								label={i18n('tickets.form.operationStatus')}
								required
								error={!!errors.operationStatus}
								helperText={errors.operationStatus ? i18n(errors.operationStatus) : ''}
							/>
						)}
						onChange={(e, newValue) => {
							setOperationStatus(newValue);

							if (errors.operationStatus) {
								setErrors((prevErrors) => ({
									...prevErrors,
									operationStatus: '',
								}));
							}
						}}
					/>
				</Grid>

				{/* <Grid item xs={12} md={3}>
					<Autocomplete
						id="impactedUsers"
						options={TICKET_IMPACTED_USERS}
						value={impactedUsers}
						disabled={loading}
						disablePortal
						openOnFocus
						renderInput={(params) => (
							<TextField
								{...params}
								label={i18n('tickets.form.impactedUsers')}
								required
								error={!!errors.impactedUsers}
								helperText={errors.impactedUsers ? i18n(errors.impactedUsers) : ''}
							/>
						)}
						onChange={(e, newValue) => {
							setImpactedUsers(newValue);

							if (errors.impactedUsers) {
								setErrors((prevErrors) => ({
									...prevErrors,
									impactedUsers: '',
								}));
							}
						}}
					/>
				</Grid> */}

				{/* <Grid item xs={12} md={3}>
					<Autocomplete
						id="financialImpact"
						options={TICKET_FINANCIAL_IMPACTS}
						value={financialImpact}
						disabled={loading}
						disablePortal
						openOnFocus
						renderInput={(params) => (
							<TextField
								{...params}
								label={i18n('tickets.form.financialImpact')}
								required
								error={!!errors.financialImpact}
								helperText={errors.financialImpact ? i18n(errors.financialImpact) : ''}
							/>
						)}
						onChange={(e, newValue) => {
							setFinancialImpact(newValue);

							if (errors.financialImpact) {
								setErrors((prevErrors) => ({
									...prevErrors,
									financialImpact: '',
								}));
							}
						}}
					/>
				</Grid> */}

				{/* <Grid item xs={12} md={3}>
					<Autocomplete
						id="priority"
						options={TICKET_PRIORITIES}
						value={priority}
						disabled={loading}
						disablePortal
						openOnFocus
						renderInput={(params) => (
							<TextField
								{...params}
								label={i18n('tickets.form.priority')}
								required
								error={!!errors.priority}
								helperText={errors.priority ? i18n(errors.priority) : ''}
							/>
						)}
						onChange={(e, newValue) => {
							setPriority(newValue);

							if (errors.priority) {
								setErrors((prevErrors) => ({
									...prevErrors,
									priority: '',
								}));
							}
						}}
					/>
				</Grid> */}
			</Grid>

			<Typography variant="h6">{i18n('tickets.form.attachments')}</Typography>

			<FileDropArea
				isDisabled={loading || LoadingAttachments || loadingSendEmail}
				files={files}
				setFiles={setFiles}
			/>

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					gap: 2,

					...(isMobile
						? {
								flexDirection: 'column',
						  }
						: {}),

					'.MuiButton-root': {
						width: 200,
						maxWidth: 200,
					},
				}}
			>
				<Button
					variant="outlined"
					disabled={loading || LoadingAttachments || loadingSendEmail}
					onClick={handleClear}
				>
					{i18n('tickets.form.clear')}
				</Button>

				<LoadingButton
					variant="contained"
					loading={loading || LoadingAttachments || loadingSendEmail}
					onClick={handleCreateTicket}
				>
					{i18n('tickets.form.create')}
				</LoadingButton>
			</Box>

			<UpdatePhoneModal
				open={showPhoneModal}
				onClose={() => setShowPhoneModal(false)}
				onSkip={() => {
					setSkipPhoneCheck(true);
					setShowPhoneModal(false);
					handleCreateTicket();
				}}
				onContinue={() => {
					setSkipPhoneCheck(true);
				}}
			/>
		</Paper>
	);
};
